import { createLogger } from "logging";

const logger = createLogger("getInitials");

//Regular expression matching characters within various types of enclosures, including the enclosures themselves
// so for example, (xyz) [xyz] {xyz} all would be ignored
const UnwantedEnclosuresRegex = /[([{][^)\]}]*[)\]}]/g;

// Regular expression matching special ASCII characters except space, plus some unicode special characters.
// Applies after unwanted enclosures have been removed
const UnwantedCharsRegex = /[!-/:-@[-`{-\u00BF\u0250-\u036F\uD800-\uFFFF]/g;

// Regular expression matching phone numbers. Applied after chars matching UNWANTED_CHARS_REGEX have been removed
// and number has been trimmed for whitespaces
const PhonenumberRegex = /^\d+[\d\s]*(:?ext|x|)\s*\d+$/i;

// Regular expression matching one or more spaces. */
const MultipleWhitespacesRegex = /\s+/g;

// Regular expression matching languages for which we currently don't support initials.
// Arabic:   Arabic, Arabic Supplement, Arabic Extended-A.
// Korean:   Hangul Jamo, Hangul Compatibility Jamo, Hangul Jamo Extended-A, Hangul Syllables, Hangul Jamo Extended-B.
// Japanese: Hiragana, Katakana.
// CJK:      CJK Unified Ideographs Extension A, CJK Unified Ideographs, CJK Compatibility Ideographs,
//           CJK Unified Ideographs Extension B
const UnsupportedTextRegex =
  /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\u1100-\u11FF\u3130-\u318F\uA960-\uA97F\uAC00-\uD7AF\uD7B0-\uD7FF\u3040-\u309F\u30A0-\u30FF\u3400-\u4DBF\u4E00-\u9FFF\uF900-\uFAFF]|[\uD840-\uD869][\uDC00-\uDED6]/;

function getInitialsLatin(displayName: string, isRtl: boolean, firstInitialOnly?: boolean): string {
  let initials = "";

  const splits: string[] = displayName.split(" ");
  if (splits.length !== 0) {
    initials += splits[0].charAt(0).toUpperCase();
  }

  if (!firstInitialOnly) {
    if (splits.length === 2) {
      initials += splits[1].charAt(0).toUpperCase();
    } else if (splits.length === 3) {
      initials += splits[2].charAt(0).toUpperCase();
    }
  }

  if (isRtl && initials.length > 1) {
    return initials.charAt(1) + initials.charAt(0);
  }

  return initials;
}

function cleanupDisplayName(s: string): string {
  let r = s.replace(UnwantedEnclosuresRegex, "");
  r = r.replace(UnwantedCharsRegex, "");
  r = r.replace(MultipleWhitespacesRegex, " ");
  r = r.trim();
  if (r !== s) {
    logger.trace(`cleanupDisplayName(): `, s, " -> ", r);
  }

  return r;
}

/**
 * Get (up to 2 characters) initials based on display name of the persona.
 *
 * @param displayName - The full name of the person or entity
 * @param isRtl - Whether the display is in RTL
 * @param options - Extra options to control the behavior of getInitials
 *
 * @returns The 1 or 2 character initials based on the name. Or an empty string if no initials
 * could be derived from the name.
 */
export function getInitials(
  displayName: string | undefined | null,
  isRtl: boolean,
  options?: {
    /** Should initials be generated from phone numbers (default false) */
    allowPhoneInitials?: boolean;

    /** Returns only the first initial */
    firstInitialOnly?: boolean;
  }
): string {
  if (!displayName) {
    return "";
  }

  displayName = cleanupDisplayName(displayName);

  // For names containing CJK characters, and phone numbers, we don't display initials
  if (UnsupportedTextRegex.test(displayName) || (!options?.allowPhoneInitials && PhonenumberRegex.test(displayName))) {
    return "";
  }

  return getInitialsLatin(displayName, isRtl, options?.firstInitialOnly);
}
